import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Truck, CheckCircle2, XCircle } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { plaqueCustomerStatus, PLAQUE_TIMELINE } from '../../lib/plaqueStatus';

function shortDate(iso) {
  if (!iso) return null;
  return iso.slice(0, 10);
}

function money(usd) {
  if (usd === null || usd === undefined) return '—';
  return `$${Number(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function OrdersTab({ memorialId }) {
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/memorials/${memorialId}/plaque-orders`)
      .then(({ data }) => setOrders(data.orders || []))
      .catch((e) => setErr(formatApiError(e)));
  }, [memorialId]);

  if (err) return <div className="ml-card text-sm text-red-700" data-testid="orders-tab-error">{err}</div>;
  if (!orders) return <div className="text-[hsl(var(--muted-foreground))]" data-testid="orders-tab-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="orders-tab">
      <div>
        <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Plaque Order Status</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Follow your acrylic or stainless steel plaque orders from review to delivery.
          We also email you at every stage.
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="ml-card text-center py-10" data-testid="orders-tab-empty">
          <Package className="w-8 h-8 mx-auto text-[hsl(var(--muted-foreground))]" strokeWidth={1.25} />
          <p className="mt-3 text-[hsl(var(--primary))]">No plaque orders yet.</p>
          <Link to={`/memorials/${memorialId}/order-plaque`} className="ml-btn-gold inline-flex mt-5" data-testid="orders-tab-order-cta">
            Order a memorial plaque
          </Link>
        </div>
      ) : (
        orders.map((o) => <OrderCard key={o.order_id} order={o} />)
      )}
    </div>
  );
}

function OrderCard({ order: o }) {
  const info = plaqueCustomerStatus(o.status);
  const cancelled = o.status === 'cancelled';
  const currentIdx = PLAQUE_TIMELINE.indexOf(o.status);
  const stageDate = {
    pending_admin_fulfillment: o.paid_at || o.created_at,
    processing: o.processing_at,
    ordered_with_supplier: o.ordered_with_supplier_at,
    shipped: o.shipped_at,
    delivered: o.delivered_at,
  };

  return (
    <div className="ml-card !p-6" data-testid={`order-card-${o.order_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="ml-overline">Order · {(o.order_id || '').replace('plaque_', '').toUpperCase()}</div>
          <h3 className="font-serif text-xl text-[hsl(var(--primary))] mt-1">
            {o.quantity} × {o.product_label}
            <span className="font-sans text-sm text-[hsl(var(--muted-foreground))] ml-2">{money(o.amount_usd)}</span>
          </h3>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
            Ordered {shortDate(o.paid_at || o.created_at) || '—'}
            {o.shipping_city ? ` · Ships to ${o.shipping_city}, ${o.shipping_state || ''}` : ''}
          </div>
        </div>
        <span
          className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full border ${
            cancelled
              ? 'bg-red-50 text-red-700 border-red-200'
              : o.status === 'delivered'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--primary))] border-[hsl(var(--accent)/0.4)]'
          }`}
          data-testid={`order-status-${o.order_id}`}
        >
          {info.label}
        </span>
      </div>

      {!cancelled && (
        <div className="mt-6" data-testid={`order-timeline-${o.order_id}`}>
          <div className="flex items-center">
            {PLAQUE_TIMELINE.map((s, i) => {
              const done = currentIdx >= i;
              return (
                <React.Fragment key={s}>
                  {i > 0 && (
                    <div className={`flex-1 h-0.5 ${currentIdx >= i ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--primary)/0.12)]'}`} />
                  )}
                  <div
                    className={`w-3.5 h-3.5 rounded-full shrink-0 border-2 ${
                      done
                        ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))]'
                        : 'bg-transparent border-[hsl(var(--primary)/0.25)]'
                    }`}
                    title={plaqueCustomerStatus(s).label}
                  />
                </React.Fragment>
              );
            })}
          </div>
          <div className="grid grid-cols-5 mt-2 text-[10px] sm:text-[11px] text-center">
            {PLAQUE_TIMELINE.map((s, i) => (
              <div key={s} className={currentIdx >= i ? 'text-[hsl(var(--primary))] font-medium' : 'text-[hsl(var(--muted-foreground))]'}>
                {plaqueCustomerStatus(s).label}
                {stageDate[s] && currentIdx >= i && (
                  <div className="text-[hsl(var(--muted-foreground))] font-normal">{shortDate(stageDate[s])}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 rounded-lg bg-[hsl(var(--primary)/0.04)] border border-[hsl(var(--primary)/0.08)] px-4 py-3 text-sm text-[hsl(var(--primary))]" data-testid={`order-next-step-${o.order_id}`}>
        {cancelled ? <XCircle className="w-4 h-4 inline mr-1.5 text-red-600" strokeWidth={1.5} />
          : o.status === 'delivered' ? <CheckCircle2 className="w-4 h-4 inline mr-1.5 text-emerald-600" strokeWidth={1.5} />
          : <Truck className="w-4 h-4 inline mr-1.5 text-[hsl(var(--accent))]" strokeWidth={1.5} />}
        {info.next}
      </div>

      {(o.tracking_number || o.carrier || o.estimated_delivery_date) && (
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm" data-testid={`order-tracking-${o.order_id}`}>
          {o.tracking_number && (
            <div>
              <div className="ml-overline !text-[10px]">Tracking</div>
              {o.tracking_url ? (
                <a href={o.tracking_url} target="_blank" rel="noreferrer" className="font-mono text-[hsl(var(--accent))] underline break-all">
                  {o.tracking_number}
                </a>
              ) : (
                <span className="font-mono">{o.tracking_number}</span>
              )}
            </div>
          )}
          {o.carrier && (
            <div>
              <div className="ml-overline !text-[10px]">Carrier</div>
              {o.carrier}
            </div>
          )}
          {o.estimated_delivery_date && (
            <div>
              <div className="ml-overline !text-[10px]">Estimated delivery</div>
              {o.estimated_delivery_date}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
